/**
 * Auth form Zod schemas (login, register, forgot/reset password).
 *
 * UI callers should prefer the `create*Schema` factories with next-intl messages.
 * Exported `*Schema` constants are English / key fallbacks for tests and non-UI callers.
 */

import { z } from 'zod';

import {
  createPasswordSchema,
  PASSWORD_MESSAGE_KEYS,
  type PasswordSchemaMessages,
} from '@/lib/validations/password';

/** i18n messages for {@link createLoginSchema} / {@link createForgotPasswordSchema} */
export interface LoginSchemaMessages {
  /** Empty required field */
  requiredField: string;
  /** Malformed email */
  invalidEmailAddress: string;
}

/**
 * Login form validation schema with caller-supplied i18n messages.
 * Prefer {@link createLoginSchema} from UI; {@link loginSchema} is an English fallback.
 *
 * @param messages - Translated validation copy
 */
export function createLoginSchema(messages: LoginSchemaMessages) {
  return z.object({
    email: z.string().min(1, messages.requiredField).email(messages.invalidEmailAddress),
    password: z.string().min(1, messages.requiredField),
  });
}

/** English fallback for non-UI callers/tests */
export const loginSchema = createLoginSchema({
  requiredField: 'This field is required.',
  invalidEmailAddress: 'Invalid email address.',
});

/** Inferred login form values from {@link createLoginSchema} */
export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

/** i18n messages for {@link createRegisterSchema} */
export interface RegisterSchemaMessages {
  /** Empty required field */
  requiredField: string;
  /** Malformed email */
  invalidEmailAddress: string;
  /** Password / confirm mismatch */
  passwordsMustMatch: string;
  /** Password complexity messages (translated or keys) */
  password: PasswordSchemaMessages;
}

/**
 * Registration form validation schema with caller-supplied i18n messages.
 * Prefer {@link createRegisterSchema} from UI; {@link registerSchema} is a fallback.
 *
 * @param messages - Translated validation copy, including password rules
 * @param countryCode - Optional country code; when 'JP', kanji fields are required
 */
export function createRegisterSchema(messages: RegisterSchemaMessages, countryCode?: string) {
  const isJP = countryCode?.toUpperCase() === 'JP';

  return z
    .object({
      title: z.string().min(1, messages.requiredField),
      firstName: z.string().min(1, messages.requiredField).max(150),
      lastName: z.string().min(1, messages.requiredField).max(150),
      firstNameKanji: isJP
        ? z.string().min(1, messages.requiredField).max(150)
        : z.string().optional(),
      lastNameKanji: isJP
        ? z.string().min(1, messages.requiredField).max(150)
        : z.string().optional(),
      email: z.string().min(1, messages.requiredField).email(messages.invalidEmailAddress),
      phonePrefix: z.string().min(1, messages.requiredField),
      phone: z.string().min(1, messages.requiredField),
      dobDay: z.string().optional(),
      dobMonth: z.string().optional(),
      dobYear: z.string().optional(),
      password: createPasswordSchema(messages.password),
      confirmPassword: z.string().min(1, messages.requiredField),
      newsletterConsent: z.boolean().optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: messages.passwordsMustMatch,
      path: ['confirmPassword'],
    });
}

/**
 * Fallback for non-UI callers/tests.
 * Password rule messages are {@link PASSWORD_MESSAGE_KEYS} (translate before display).
 */
export const registerSchema = createRegisterSchema({
  requiredField: 'This field is required.',
  invalidEmailAddress: 'Invalid email address.',
  passwordsMustMatch: 'Passwords must match.',
  password: PASSWORD_MESSAGE_KEYS,
});

/** Inferred register form values from {@link createRegisterSchema} */
export type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

/**
 * Forgot-password form validation schema with caller-supplied i18n messages.
 *
 * @param messages - Translated validation copy (same shape as login email field)
 */
export function createForgotPasswordSchema(messages: LoginSchemaMessages) {
  return z.object({
    email: z.string().min(1, messages.requiredField).email(messages.invalidEmailAddress),
  });
}

/** English fallback for non-UI callers/tests */
export const forgotPasswordSchema = createForgotPasswordSchema({
  requiredField: 'This field is required.',
  invalidEmailAddress: 'Invalid email address.',
});

/** Inferred forgot-password form values from {@link createForgotPasswordSchema} */
export type ForgotPasswordFormData = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

/** i18n messages for {@link createResetPasswordSchema} */
export interface ResetPasswordSchemaMessages {
  /** Empty required field */
  requiredField: string;
  /** Password / confirm mismatch */
  passwordsMustMatch: string;
  /** Password complexity messages (translated or keys) */
  password: PasswordSchemaMessages;
}

/**
 * Reset password form validation schema with caller-supplied i18n messages.
 *
 * @param messages - Translated validation copy, including password rules
 */
export function createResetPasswordSchema(messages: ResetPasswordSchemaMessages) {
  return z
    .object({
      password: createPasswordSchema(messages.password),
      confirmPassword: z.string().min(1, messages.requiredField),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: messages.passwordsMustMatch,
      path: ['confirmPassword'],
    });
}

/**
 * Fallback for non-UI callers/tests.
 * Password rule messages are {@link PASSWORD_MESSAGE_KEYS} (translate before display).
 */
export const resetPasswordSchema = createResetPasswordSchema({
  requiredField: 'This field is required.',
  passwordsMustMatch: 'Passwords must match.',
  password: PASSWORD_MESSAGE_KEYS,
});

/** Inferred reset-password form values from {@link createResetPasswordSchema} */
export type ResetPasswordFormData = z.infer<ReturnType<typeof createResetPasswordSchema>>;
